/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { CommonModule, isPlatformBrowser, Location, NgOptimizedImage, PlatformLocation } from '@angular/common';
import {
  afterNextRender,
  AfterViewInit,
  Component,
  ComponentRef,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  QueryList,
  Signal,
  signal,
  TransferState,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
  viewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { isReadable, TinyColor } from '@ctrl/tinycolor';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBars, faTimes, faPlay, faGlobe, faChevronUp, faPen } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ENVIRONMENT, MovieSiteHeaderImg } from '@environment';
import { GsapAnimationService, MovieSiteConfettiMode, playMovieSiteConfetti } from '@usheru-hff/core/gsap';
import {
  MovieSiteConfigurationService,
  MovieSiteTopMenuComponent,
  MovieSiteLandingComponent,
  NavAction,
  MovieSiteMenuLandingComponent,
  useMovieSiteEditorEffect,
  MovieSiteFooterComponent,
  MovieSiteSideMenuComponent
} from '@usheru-hff/core/movie-site';
import { MovieSiteEditorService } from '@usheru-hff/core/movie-site/utils/form-editor.service';
import { HffCommonUiGoToTopModule } from '@usheru-hff/hff-common/ui-go-to-top';
import {
  FeatureFilmInteractionsComponent,
  FeatureFilmInteractionsModule
} from '@usheru-hff/movie-detail/feature-film-interactions';
import { FeatureGalleryCarouselComponent } from '@usheru-hff/movie-detail/feature-gallery-carousel';
import {
  MovieCreditsComponent,
  MovieCompetitionComponent,
  MovieMoreInformationComponent,
  MovieReviewsComponent,
  MovieTellFriendsComponent
} from '@usheru-hff/movie-detail/ui-movie-elements';
import { NewsFeatureNewsCarouselModule } from '@usheru-hff/news/feature-news-carousel';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { MOVIE_TRANSFER_KEY, MovieTranslationsService } from '@usheru-hff/sdk/movie';
import { StructuredDataService } from '@usheru-hff/sdk/structured-data';
import {
  attachTrackingToDescription,
  generateMovieTrackingEvent,
  TRACKING_SERVICE,
  TrackingAction
} from '@usheru-hff/sdk/tracking';
import { isMobile$ } from '@usheru-hff/sdk/utils';
import { getChannelMovieProp, getMovieGenres, getMovieTags, Movie } from '@usheru-hff/shared/domain';
import { SharedFeatureCalendarButtonComponent } from '@usheru-hff/shared/feature-calendar-button';
import { SharedFeatureSocialLinksModule } from '@usheru-hff/shared/feature-social-links';
import { IntersectionObserverService, IntersectionObserverDirective } from '@usheru-hff/shared/intersection-observable';
import { FeatureFilmInteractionsToken } from '@usheru-hff/shared/tokens';
import { VideoFullscreenService } from '@usheru-hff/shared/ui-video';
import { getDateFromDDMMYYYY, LogService, ScopedLogger, SharedUtilsModule } from '@usheru-hff/shared/utils';
import { ImageService, SharedUtilsImageModule } from '@usheru-hff/shared/utils-image';
import { MetaTags, MetatagsService } from '@usheru-hff/shared/utils-metatag';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import { TitleService } from '@usheru-hff/shared/utils-title';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';
import { RemoveColorsHtmlPipe } from '@usheru-hff/ui/remove-colors-html';

import { BrandMovieService } from '../brand-movie.service';
import { textureConfig } from '../movie-site-background-texture';
import { AssetsLockerLinkComponent } from 'libs/assets-locker/ui-assets-locker-link/src/lib/access-locker-link/assets-locker-link.component';

type StatusType = 'book-now' | 'calendar' | 'watch-now';
type TrailerButtonNavActionStyle = Extract<NavAction['style'], 'button-fill' | 'button-not-fill'>;

//

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'feature-movie-awareness-page',
  imports: [
    CommonModule,
    NgOptimizedImage,
    MovieCreditsComponent,
    MovieCompetitionComponent,
    MovieMoreInformationComponent,
    MovieTellFriendsComponent,
    SharedFeatureSocialLinksModule,
    FeatureGalleryCarouselComponent,
    FontAwesomeModule,
    NewsFeatureNewsCarouselModule,
    IntersectionObserverDirective,
    MovieTellFriendsComponent,
    SharedUtilsImageModule,
    SharedUtilsModule,
    MovieSiteTopMenuComponent,
    MovieSiteLandingComponent,
    MovieSiteMenuLandingComponent,
    HffCommonUiGoToTopModule,
    MovieSiteFooterComponent,
    RemoveColorsHtmlPipe,
    MovieReviewsComponent,
    FeatureFilmInteractionsModule
  ],
  // remove if remove swiper
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './feature-movie-awareness-page.component.html',
  styleUrls: ['./feature-movie-awareness-page.component.scss']
})
export class FeatureMovieAwarenessPageComponent implements OnInit, AfterViewInit, OnDestroy {
  movie = input<Movie | undefined>(undefined);

  protected readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
  private readonly PLATFORM_ID = inject(PLATFORM_ID);
  private readonly isPlatformBrowser = isPlatformBrowser(this.PLATFORM_ID);
  private readonly platformLocation = inject(PlatformLocation);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly brandMovieService = inject(BrandMovieService);
  private readonly movieSiteConfig = inject(MovieSiteConfigurationService);
  private readonly channelService = inject(ChannelService);
  private readonly trackingService = inject(TRACKING_SERVICE);
  private readonly intersectionObserve = inject(IntersectionObserverService);
  private readonly videoService = inject(VideoFullscreenService);
  private readonly metaTagsService = inject(MetatagsService);
  private readonly imgService = inject(ImageService);
  private readonly translationsService = inject(TranslationsService);
  private readonly showtimeStreamingService = inject(ShowtimeStreamingService);
  private readonly titleService = inject(TitleService);
  private readonly structuredDataService = inject(StructuredDataService);
  private readonly movieSiteEditorService = inject(MovieSiteEditorService);
  private readonly movieTranslationsService = inject(MovieTranslationsService);
  private readonly env = inject(ENVIRONMENT);
  private readonly transferState = inject(TransferState);
  private readonly logger: ScopedLogger = inject(LogService).scoped('movie-site-awareness');
  private readonly destroyRef = inject(DestroyRef);
  private readonly gsapAnimationService = inject(GsapAnimationService);
  private readonly confettiContainer = viewChild<ElementRef<HTMLElement>>('movieSiteConfetti');
  private confettiStarted = false;

  readonly logoExternalLink: string | undefined = this.env?.movieAwarenessLogoExternalLink;
  readonly isMobile$ = isMobile$();

  @HostBinding('style') get bindingStyle() {
    // return this.cssStyleSignal();
    return this.movieSiteConfig.getCSSStyleFromTheme();
  }

  @HostBinding('attr.data-enable-scroll-snap') get enableScrollSnap() {
    return this.pageConfiguration()?.disableScrollAnimations || this.horizontalGalleryScrollEnabled()
      ? 'false'
      : 'true';
  }

  @ViewChildren('section') sectionsQueryList!: QueryList<ElementRef<HTMLElement>>;

  @ViewChildren(IntersectionObserverDirective, { read: ElementRef<HTMLElement> }) intersections!: QueryList<
    ElementRef<HTMLElement>
  >;
  @ViewChildren(IntersectionObserverDirective) intersectionsDirectives!: QueryList<IntersectionObserverDirective>;
  @ViewChild(MovieSiteSideMenuComponent) sidemenuComponent!: MovieSiteSideMenuComponent;
  @ViewChild('storyline') sectionStorylineRef!: ElementRef<HTMLElement>;
  @ViewChild('gallery') sectionGalleryRef!: ElementRef<HTMLElement>;
  @ViewChild('descriptionContainer', { static: false }) descriptionContainerRef!: ElementRef;
  @ViewChild('filmInteractionComponentDesktop', { read: ViewContainerRef })
  filmInteractionComponentDesktop!: ViewContainerRef;
  @ViewChild('filmInteractionComponentMobile', { read: ViewContainerRef })
  filmInteractionComponentMobile!: ViewContainerRef;

  private filmInteractionComponentRef!: ComponentRef<FeatureFilmInteractionsComponent>;
  private filmInteractionComponent2Ref!: ComponentRef<FeatureFilmInteractionsComponent>;

  /*   movie: Signal<Movie | null | undefined>; */
  status: Signal<StatusType[]>;
  releaseDate: Signal<Date | undefined>;
  brandedMovieStyle = signal({});
  isBrandColorReadable = signal<boolean>(true);
  sectionStorylineVisibleClass = signal<string[]>([]);
  readonly desktopBreakpoint = 1200;
  readonly visibilityAnimatonClass = 'ng-visible';
  readonly sectionStorylineEnterThreshold = 20;
  auxiliarMenuVisible = signal<boolean>(false);
  mainMenuVisibleOutput = new BehaviorSubject<boolean>(false);
  mainMenuVisibleOutput$!: Subscription;
  buttonToTopVisible = signal<boolean>(false);

  filmInteractionIsEnabled = signal(false);

  readonly showCompetitionInAwareness = computed(() =>
    this.brandMovieService.shouldShowCompetitionInAwareness(this.movie())
  );

  navActions = computed<NavAction[]>(() => this.generateNavActions());

  /**
   * IMPORTANT NOTE: This filter condition was bad redacted and it is **confused**.
   *
   * The current behavior should be incorrect to what supposed to be, but nobody complain about.
   *
   * It was fixed and was reverted to mantain the behavior of previous versions. Team explicit petition.
   */
  landingActions = computed<NavAction[]>(() =>
    this.navActions().filter(action => action.type !== 'link' || action.placeholder !== 'Home')
  );
  /**
   * IMPORTANT NOTE: This filter condition was bad redacted and it is **confused**.
   *
   * The current behavior should be incorrect to what supposed to be, but nobody complain about.
   *
   * It was fixed and was reverted to mantain the behavior of previous versions. Team explicit petition.
   */
  topMenuActions = computed<NavAction[]>(() =>
    this.navActions().filter(action => action.type !== 'component' || action.placeholder !== 'Calendar')
  );
  auxiliarActions = computed<NavAction[]>(() => this.navActions());

  /**
   * @deprecated
   */
  sectionsHyperLink = signal<SectionHyperLink[]>([
    {
      label: $localize`:@@movie.menu.home:Home`,
      placeholder: 'Home',
      ref: 'home'
    },
    {
      label: $localize`:@@movie.menu.about:About`,
      placeholder: 'About',
      ref: 'storyline'
    }
  ]);

  readonly pageConfiguration = this.movieSiteConfig.pageConfiguration;
  readonly confettiMode = computed<MovieSiteConfettiMode | undefined>(() => {
    const movie = this.movie();
    if (!movie || this.pageConfiguration()?.disableScrollAnimations) return undefined;

    const mode = getChannelMovieProp('movieSite.gsap.confetti', this.channelService.channel, movie);
    return mode === 'enabled' ? mode : undefined;
  });
  readonly synopsisConfiguration = this.movieSiteConfig.synopsisConfiguration;
  readonly topMenuConfiguration = this.movieSiteConfig.topMenuConfiguration;

  landingFillScreen = signal<boolean>(true);
  customHeaderImages = signal<MovieSiteHeaderImg[] | undefined>(undefined);

  today = new Date().getTime();
  observers?: string[];
  routePath = '.';
  readonly gallerySlidesPerView = computed(() => this.getGallerySlidesPerView());
  readonly galleryAspectRatio = computed(() =>
    getChannelMovieProp('movieSite.gallery.aspectRatio', this.channelService.channel, this.movie())
  );
  readonly galleryPosition = computed(() =>
    getChannelMovieProp('movieSite.gallery.position', this.channelService.channel, this.movie())
  );
  readonly backgroundTexture = computed(() =>
    getChannelMovieProp('movieSite.background.texture', this.channelService.channel, this.movie())
  );
  readonly backgroundTextureConfig = computed(() => {
    const texture = this.backgroundTexture();
    return texture ? textureConfig[texture] : null;
  });
  readonly horizontalGalleryScrollEnabled = computed(() => {
    const movie = this.movie();
    if (!movie?.movieImages?.length) {
      return false;
    }

    return getChannelMovieProp('movieSite.gsap.horizontalScrollGallery', this.channelService.channel, movie) ?? false;
  });

  appHasOnlyMovies = false;
  readonly TrackingCategory = 'MovieSite';
  readonly faPlay = faPlay;
  readonly faMenu = faBars;
  readonly faClose = faTimes;
  readonly faGlobe = faGlobe;
  readonly faChevronUp = faChevronUp;
  readonly faPen = faPen;

  readonly movieAwarenessBackgroundImageFit = this.env.movieAwarenessBackgroundImageFit;
  readonly creditsPlacement = computed(
    () => this.movie()?.properties?.movieSite?.awareness?.creditsPlacement ?? 'synopsis'
  );
  readonly disableImdb = computed(() => this.getDisableMoreInfoElement('disableImdb'));
  readonly disableTmdb = computed(() => this.getDisableMoreInfoElement('disableTmdb'));

  // Pure translations object, independent from movie
  readonly movieTranslations = this.movieTranslationsService.translations;
  readonly hasMovieTranslationsBeenLoaded = this.movieTranslationsService.hasLoadedTranslations;

  /**
   * Synopsis heading. `synopsis.headerText` is the movie-translation key.
   * Without a movie translation, the heading stays `movie.menu.synopsis`.
   */
  readonly synopsisHeader = computed(() => {
    const headerKey = this.synopsisConfiguration().headerText || 'movie.menu.synopsis';
    const movieTranslation = this.movieTranslations()?.[headerKey];

    if (movieTranslation) {
      return { key: headerKey, text: movieTranslation };
    }

    return {
      key: 'movie.menu.synopsis',
      text: $localize`:@@movie.menu.synopsis:Summary`
    };
  });

  hideLogoTitle: boolean | undefined = false;
  hideTitleText: boolean | undefined = false;
  hideReleaseMessage: boolean | undefined = false;
  attachReleaseMessageToLogo: boolean | undefined = false;
  disableGradient = false;

  readonly movieShowtimeLink: string | undefined;

  public readonly isEditEnabled = this.movieSiteEditorService.isEnabled;
  private suppressSectionFragmentUpdates = false;
  private sectionFragmentUpdateSuppressionTimeout?: number;
  private readonly sectionFragmentUpdateSuppressionMs = 1000;

  @HostListener('window:resize')
  onResize() {
    this.intersectionObserve.checkBreakpoints();
  }

  @HostListener('window:scroll')
  onScroll() {
    // this.setFixTopMovieImageForMobile();
  }

  get element() {
    return this.elementRef.nativeElement;
  }

  private readonly assetsLockerKey = 'movie-site.assets-locker';

  private isLoadingAssetsTranslations = signal<boolean>(true);
  private assetsLockerLinkTranslation = signal<string>($localize`:@@movie-site.assets-locker:Assets Locker`);

  constructor(
    private location: Location,
    @Optional() private featureFilmInteractionToken: FeatureFilmInteractionsToken
  ) {
    if (this.isPlatformBrowser) {
      if (!this.route.snapshot.fragment) {
        window.scroll({ top: 0, behavior: 'instant' });
      }
      this.movieShowtimeLink = window.location.href.replace('/film-info', '/book-now').split('#')[0];
    }

    useMovieSiteEditorEffect();
    // Prepare configuration setting
    effect(
      () => {
        if (!this.movie()) return;
        this.movieSiteConfig.prepareService(this.movie()!, 'film-info');
      },
      { allowSignalWrites: true }
    );

    effect(() =>
      this.structuredDataService.setMovieJsonLd(
        this.movie(),
        this.isPlatformBrowser ? this.platformLocation.href : undefined
      )
    );

    effect(
      () => {
        this.isLoadingAssetsTranslations.set(false);
        if (!this.movieTranslationsService.hasMovieTranslations()) {
          this.isLoadingAssetsTranslations.set(false);

          return;
        }

        this.isLoadingAssetsTranslations.set(true);

        const movieTranslations = this.movieTranslations();

        if (movieTranslations && movieTranslations[this.assetsLockerKey]) {
          this.assetsLockerLinkTranslation.set(movieTranslations[this.assetsLockerKey]);
        }

        this.isLoadingAssetsTranslations.set(!this.hasMovieTranslationsBeenLoaded());
      },
      { allowSignalWrites: true }
    );

    this.movieSiteConfig.effectMetaTitleFromMovieTranslations({
      movie: this.movie,
      translations: this.movieTranslations,
      hasLoaded: this.hasMovieTranslationsBeenLoaded
    });
    /**
     * If for some reason the user reach the route without the awarness path. It will be added
     * to the url and the user will be redirected to the correct path.
     */
    // if (this.route.snapshot.url[0]?.path !== 'film-info') {
    //   this.router.navigate([this.router.url, 'film-info'], {
    //     replaceUrl: true
    //  });
    // }

    this.appHasOnlyMovies = this.env?.appHasOnlyMovies || false;

    this.status = computed(() => {
      const status: StatusType[] = [];
      const locReleaseDate = this.movie()?.locReleaseDate;
      const releaseDate = this.movie()?.releaseDate;
      if (!locReleaseDate && !releaseDate) return [];

      const release = getDateFromDDMMYYYY(locReleaseDate! || releaseDate!).getTime();

      if (release > this.today && !this.topMenuConfiguration()?.preventCalendar) {
        status.push('calendar');
      }

      if (this.hasShowtimes) {
        status.push('book-now');
      } else if (this.hasStreaming) {
        status.push('watch-now');
      }
      return status;
    });

    this.releaseDate = computed(() => {
      const release = this.movie()?.locReleaseDate || this.movie()?.releaseDate;
      return release ? getDateFromDDMMYYYY(release) || undefined : undefined;
    });
    //

    //
    // check if the route ends with 'film-info'
    const url = this.route.snapshot.url;
    this.routePath = url[url.length - 1].path === 'film-info' ? '..' : '.';

    afterNextRender(() => {
      void this.startMovieSiteConfetti();
    });

    effect(() => {
      this.confettiMode();
      this.confettiContainer();
      if (!this.isPlatformBrowser) return;
      void this.startMovieSiteConfetti();
    });
  }

  ngOnInit() {
    this.movieSiteConfig.prepareService(this.movie()!, 'film-info');

    this.isBrandColorReadable.set(this.isRedeableBrandOverBackgroundColor());

    this.setSectionsHyperlink();
  }

  ngAfterViewInit() {
    const hasInitialFragment = !!this.route.snapshot.fragment;
    if (!hasInitialFragment) {
      this.scrollTop();
    }
    this.checkAnchor();
    if (hasInitialFragment) {
      this.temporarilySuppressSectionFragmentUpdates();
    }
    if (this.isPlatformBrowser) {
      this.activeIntersectionAnimation();
    }
    this.setAuxiliarMenuVisiblility();
    if (this.movie()?.faviconImageUrl) {
      this.titleService.setFavicon(this.movie()?.faviconImageUrl);
    }

    attachTrackingToDescription(this.trackingService, 'click', this.descriptionContainerRef);

    this.loadCommunityElements();
  }

  scrollTop() {
    if (this.isPlatformBrowser) {
      this.elementRef.nativeElement.scroll({ top: 0, behavior: 'smooth' });
    }
  }

  // component.ts
  containerStyle: { [prop: string]: string } = {
    width: '100vw',
    height: '100vh'
  };

  onImageLoad(image: HTMLImageElement) {
    const imageWidth = image.naturalWidth; // e.g., 1481
    const imageHeight = image.naturalHeight; // e.g., 833
    const imageRatio = imageWidth / imageHeight; // ~1.7787

    // Container ratio if we did 100vw x 100vh
    const containerRatio = this.isPlatformBrowser ? window.innerWidth / window.innerHeight : 16 / 9;
    const ratioDelta = imageRatio / containerRatio;

    // Some threshold to decide if we're drastically cropping
    const THRESHOLD = 1.2;

    if (ratioDelta > THRESHOLD) {
      // Image is significantly "wider" than container
      // Keep container full width = 100vw, but adjust height so ratio matches
      // height = (100vw / imageRatio)
      // But we can't directly do: 'height: `calc(100vw / 1.7787)`' unless we convert 1.7787 into a fraction.
      // In plain CSS we can do `calc(100vw / 1.7787)`, but let's be dynamic in JS:

      this.containerStyle = {
        width: '100vw',
        height: `calc(100vw / ${imageRatio})`
      };
    } else if (ratioDelta < 1 / THRESHOLD) {
      // Image is significantly "taller" than container
      // Keep container full height = 100vh, but adjust width so ratio matches
      // width = (100vh * imageRatio)
      this.containerStyle = {
        width: `calc(100vh * ${imageRatio})`,
        height: '100vh'
      };
    } else {
      // Ratios are close enough -- stick with 100vw x 100vh
      this.containerStyle = {
        width: '100vw',
        height: '100vh'
      };
    }
  }

  setSectionsHyperlink() {
    if (!this.appHasOnlyMovies && (this.movie()?.newsPosts?.length || 0 > 0)) {
      this.sectionsHyperLink.update(hyperlinks => {
        hyperlinks.push({
          label: $localize`:@@movie-template-a.menu.news:News`,
          placeholder: 'News',
          ref: 'news'
        });

        return hyperlinks;
      });
    }

    if (this.movie()?.movieImages) {
      this.sectionsHyperLink.update(hyperlinks => {
        hyperlinks.push({
          label: $localize`:@@movie-template-a.menu.gallery:Gallery`,

          placeholder: 'Gallery',

          ref: 'gallery'
        });

        return hyperlinks;
      });
    }
  }

  setAuxiliarMenuVisiblility() {
    if (this.isPlatformBrowser) {
      this.mainMenuVisibleOutput$ = this.mainMenuVisibleOutput.subscribe(value => {
        this.auxiliarMenuVisible.set(!value);
        this.buttonToTopVisible.set(!value);
      });
    }
  }

  checkAnchor() {
    const fragment = this.route.snapshot.fragment;
    if (!fragment) return;

    const section = this.getSection(fragment);
    if (!section) return;

    this.brandMovieService.goToSection(section, 'instant');
  }

  private temporarilySuppressSectionFragmentUpdates() {
    if (!this.isPlatformBrowser) return;

    this.suppressSectionFragmentUpdates = true;
    window.clearTimeout(this.sectionFragmentUpdateSuppressionTimeout);
    this.sectionFragmentUpdateSuppressionTimeout = window.setTimeout(() => {
      this.suppressSectionFragmentUpdates = false;
    }, this.sectionFragmentUpdateSuppressionMs);
  }

  getSection(ref: string): HTMLElement | null {
    return this.sectionsQueryList.find(el => el.nativeElement.id === ref)?.nativeElement || null;
  }

  goToSection(refEl: string, behavior: ScrollBehavior = 'smooth') {
    const section = this.getSection(refEl);

    if (!section) return;

    this.brandMovieService.goToSection(section, behavior, {
      section: refEl,
      movie: this.movie()!
    });
  }

  activeIntersectionAnimation() {
    const elements = this.intersections.toArray().map(el => el.nativeElement);
    const els = elements.filter(el => !el.getAttribute('intersectionref'));
    const menuEls = elements.filter(el => el.getAttribute('intersectionref') === 'menu');
    const noThresholdEls = elements.filter(el => el.getAttribute('intersectionref') === 'no-threshold');
    this.observers = [];
    this.observers.push(
      this.intersectionObserve.observe(els, {
        breakpoint: this.desktopBreakpoint,
        threshold: 0.05
      })
    );

    this.observers.push(
      this.intersectionObserve.observe(noThresholdEls, {
        breakpoint: this.desktopBreakpoint,
        threshold: 0
      })
    );

    this.observers.push(
      this.intersectionObserve.observe(menuEls, {
        breakpoint: this.desktopBreakpoint,
        threshold: 0.01,
        visibilityOutput: this.mainMenuVisibleOutput
      })
    );
    // SECTIONS
    const sectionsEls = elements.filter(el => el.getAttribute('intersectionref') === 'section');

    if (this.isPlatformBrowser) {
      this.observers.push(
        // Observe to update the fragment in the url
        this.intersectionObserve.observe(sectionsEls, {
          threshold: 0.5,
          callback: (entries: IntersectionObserverEntry[]) => {
            entries.forEach(entry => {
              /**
               * The need make this only over desktop breakpoint is because the mobile version
               * the sections don't have a minimum height to be visible in the viewport
               * so the intersection observer will always be intersecting causing the fragment
               * to be updated in the url
               */
              if (entry.isIntersecting && window.innerWidth >= this.desktopBreakpoint) {
                if (this.suppressSectionFragmentUpdates) {
                  return;
                }

                const fragment = entry.target.id;
                this.router
                  .navigate([], {
                    fragment,
                    replaceUrl: true,
                    queryParamsHandling: 'merge'
                  })
                  .then(() => {
                    if (entry.target.id === 'landing') {
                      this.location.replaceState(this.router.url.split('#')[0]);
                    }
                  });
              }
            });
          }
        })
      );
    }
  }

  playTrailer() {
    this.brandMovieService.closeSidemenu();
    this.videoService.playWithConsent({
      content: this.movie() as Movie,
      category: this.TrackingCategory,
      animate: true,
      videoSettings: { muted: false, autoplay: true }
    });
  }

  private getTrailerButtonNavActionStyle(): TrailerButtonNavActionStyle {
    return this.movie()?.properties?.movieSite?.awareness?.trailerButtonFilled ? 'button-fill' : 'button-not-fill';
  }

  setMetaTags(movie?: Movie): void {
    if (!movie) {
      this.metaTagsService.resetMetaTags();
      return;
    }

    //

    const metaTags: MetaTags = {
      url: this.platformLocation.pathname,
      title: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.title`)
        ? `movie.${movie.friendlyUrl}.title`
        : $localize`:@@meta.page.movie-detail.title:_channel_ - _movie_`,
      description: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.description`)
        ? `movie.${movie.friendlyUrl}.description`
        : $localize`:@@meta.page.movie-detail.description:Movie detail`,

      video: movie.trTrailerLink
    };
    metaTags.favicon = movie?.faviconImageUrl;
    if (movie.tilePhotoUrl) {
      metaTags.image = this.imgService.computeUrl(movie.tilePhotoUrl, { width: 1280 });
    } else if (movie.portraitPhotoUrl) {
      metaTags.image = this.imgService.computeUrl(movie.portraitPhotoUrl, { height: 1000 });
    }
    //TODO cases with more directors?
    const director = movie?.movieCrew?.find(mc => {
      if (!mc.crewRole?.name) return false;

      return mc.crewRole.name.toLowerCase() == 'director';
    })?.star?.name;

    this.metaTagsService.setMetaTags(metaTags, {
      movie: movie.title,
      director: director
    });
  }

  /**
   * Used to determine if the brand color is readable over the background color and change for a better color if needed
   */

  isRedeableBrandOverBackgroundColor() {
    const brandColor = new TinyColor(this.movie()?.properties?.theme?.mainColor || '#000000');
    const backgroundColor = new TinyColor(this.movie()?.properties?.theme?.backgroundColor || '#ffffff');
    return isReadable(brandColor, backgroundColor);
  }

  get trackingEventCalendar() {
    // The 'calendar' prop is added in the calendar action
    return {
      action: 'AddToCalendar',
      properties: {
        label: this.movie()!.title!,
        category: 'MovieDetail',
        movie: this.movie()!.title!,
        idMovie: this.movie()!.id!,
        imdb: this.movie()!.imdb!,
        tagsMovie: getMovieTags(this.movie()!),
        genresMovie: getMovieGenres(this.movie()!)
      }
    };
  }

  get hasShowtimes() {
    return this.movie() && this.showtimeStreamingService.determineInCinemasAvailability(this.movie()!);
  }

  get hasStreaming() {
    return this.movie() && this.showtimeStreamingService.determineStreamingAvailability(this.movie()!);
  }

  addBindingStyle(rule: string, value: string) {
    this.bindingStyle[rule] = value;
  }

  removeBindingStyle(rule: string) {
    delete this.bindingStyle[rule];
  }

  private getDisableMoreInfoElement(property: 'disableImdb' | 'disableTmdb'): boolean {
    const movie = this.movie();
    if (!movie) return false;

    return (
      movie.properties?.movieSite?.awareness?.[property] ??
      this.env.movieSiteConfig?.[movie.friendlyUrl]?.[property] ??
      false
    );
  }

  get hasAssets() {
    return this.movie()?.hasAssets || false;
  }

  private startMovieSiteConfetti(): void {
    if (this.confettiStarted || !this.isPlatformBrowser || !this.gsapAnimationService.canAnimate()) return;

    const mode = this.confettiMode();
    const container = this.confettiContainer()?.nativeElement;
    if (!mode || !container) return;

    this.confettiStarted = true;
    void playMovieSiteConfetti(container, this.destroyRef, this.gsapAnimationService);
  }

  ngOnDestroy(): void {
    if (this.isPlatformBrowser) {
      window.clearTimeout(this.sectionFragmentUpdateSuppressionTimeout);
    }

    this.movieSiteConfig.resetService();
    this.metaTagsService.resetMetaTags();
    this.brandMovieService.removeStyles();
    this.brandMovieService.restoreFooter();
    this.structuredDataService.removeJsonLdScript();

    this.transferState.remove(MOVIE_TRANSFER_KEY);

    if (this.observers) this.intersectionObserve.clean(this.observers);
    this.mainMenuVisibleOutput$?.unsubscribe();
  }

  // NEW CODE TO CLEAN AFTER
  generateNavActions(): NavAction[] {
    /**
     * IMPORTANT:There is an issue related to 'visible-sm'.
     *
     * Action could be render but hidden
     *
     * It was fixed and was reverted to mantain the behavior of previous versions. Team explicit petition.
     *
     * The comment is still here for future reference.
     *
     * The not-fix way to display the a action is:
     * 1. create a property like `displayAboutButtonInDesktop`
     * 2. use that property to set the `extraClasses` class `visible-sm-fix` to the action
     */

    const movie = this.movie();
    const navActions: NavAction[] = [];
    const matchPrimaryActionButtonsToCalendar =
      movie?.properties?.movieSite?.awareness?.matchPrimaryActionButtonsToCalendar ?? false;
    const primaryLinkStyle = matchPrimaryActionButtonsToCalendar ? 'button-not-fill' : 'button-fill';

    function getTrackingEvent(action: TrackingAction, category: string) {
      return generateMovieTrackingEvent(movie!, action, category);
    }

    // Add Home
    //if (!this.channelService.channel.properties?.movieSite?.hideHomeSectionLink) {
    navActions.push({
      type: 'link',
      label: $localize`:@@movie.menu.home:Home`,
      labelKey: 'movie.menu.home',
      placeholder: 'Home',
      url: [],
      fragment: 'landing',
      style: 'inline',
      tracking: getTrackingEvent('HomeView', 'MovieDetail')
    });
    //}
    // Add About

    const hasDisplayAboutButtonProp =
      getChannelMovieProp('landing.displayAboutButtonInDesktop', this.channelService.channel, movie) ?? false;

    navActions.push({
      type: 'link',
      label: $localize`:@@movie.menu.about:About`,
      labelKey: 'movie.menu.about',
      placeholder: 'About',
      url: [],
      fragment: 'storyline',
      style: hasDisplayAboutButtonProp ? 'button-not-fill' : 'inline',
      tracking: getTrackingEvent('AboutView', 'MovieDetail'),
      extraClasses: {
        'visible-sm-fix': hasDisplayAboutButtonProp
      }
    });
    // Add Info

    //TODO (?)

    // Add News
    if (!this.appHasOnlyMovies && this.isNewsEnabled) {
      navActions.push({
        type: 'link',
        label: $localize`:@@movie.menu.news:News`,
        labelKey: 'movie.menu.news',
        placeholder: 'News',
        url: [],
        fragment: 'news',
        style: 'inline',
        tracking: getTrackingEvent('NewsView', 'MovieDetail')
      });
    }

    // Add Competition
    if (this.showCompetitionInAwareness()) {
      navActions.push({
        type: 'link',
        label: $localize`:@@movie.cta.enterNowToWin:Enter now to win`,
        labelKey: 'movie.cta.enterNowToWin',
        placeholder: 'Enter now to win',
        url: [],
        fragment: 'competition',
        style: primaryLinkStyle,
        extraClasses: {
          'primary-action-button': matchPrimaryActionButtonsToCalendar,
          'visible-sm': true
        },
        tracking: getTrackingEvent('CompetitionPageView', 'MovieDetail')
      });
    }

    // Add Gallery
    if (this.movie()?.movieImages) {
      navActions.push({
        type: 'link',
        label: $localize`:@@movie-template-a.menu.gallery:Gallery`,
        labelKey: 'movie-template-a.menu.gallery',
        placeholder: 'Gallery',
        url: [],
        fragment: 'gallery',
        style: 'inline',
        tracking: getTrackingEvent('GalleryView', 'MovieDetail')
      });
    }
    // Add Trailer
    if (this.movie()?.trailerLink) {
      navActions.push({
        type: 'command',
        label: $localize`:@@movie.menu.trailer:Trailer`,
        labelKey: 'movie.menu.trailer',
        placeholder: 'Trailer',
        icon: faPlay,
        command: () => this.playTrailer(),
        style: matchPrimaryActionButtonsToCalendar ? primaryLinkStyle : this.getTrailerButtonNavActionStyle(),
        extraClasses: {
          'primary-action-button': matchPrimaryActionButtonsToCalendar,
          'visible-sm': true
        },
        tracking: getTrackingEvent('TrailerView', 'MovieDetail')
      });
    }
    // Add Book Now Link
    if (this.status().includes('book-now')) {
      navActions.push({
        type: 'link',
        label: $localize`:@@movie-template-a.menu.inCinemas:Book Now`,
        labelKey: 'movie-template-a.menu.inCinemas',
        placeholder: 'Book Now',
        url: [this.routePath, 'book-now'],
        style: primaryLinkStyle,
        extraClasses: {
          'primary-action-button': matchPrimaryActionButtonsToCalendar,
          'visible-sm': true
        },
        tracking: getTrackingEvent('BookNowView', 'MovieDetail')
      });
    }
    // Add Watch Now Link
    if (this.status().includes('watch-now')) {
      navActions.push({
        type: 'link',
        label: $localize`:@@movie-template-a.menu.watchAtHome:Watch Now`,
        labelKey: 'movie-template-a.menu.watchAtHome',
        placeholder: 'Watch Now',
        url: [this.routePath, 'watch-now'],
        style: primaryLinkStyle,
        extraClasses: {
          'primary-action-button': matchPrimaryActionButtonsToCalendar,
          'visible-sm': true
        }
      });
    }

    // Add Calendar Link
    if (this.status().includes('calendar')) {
      navActions.push({
        type: 'component',
        label: $localize`:@@movie-template-a.menu.calendar:Calendar`,
        labelKey: 'movie-template-a.menu.calendar',
        placeholder: 'Calendar',
        component: SharedFeatureCalendarButtonComponent,
        binding: {
          subject: `${this.movie()?.title} release`,
          text: this.movieShowtimeLink,
          date: this.releaseDate(),
          trackingEvent: this.trackingEventCalendar,
          classes: [
            'movie-site_nav-action_menu-action',
            'not-fill',
            ...(matchPrimaryActionButtonsToCalendar ? ['primary-action-button'] : []),
            'visible-sm'
          ]
        }
      });
    }

    // Add Download Assets Link
    if (this.movie()?.hasAssets) {
      navActions.push({
        type: 'component',
        label: this.assetsLockerLinkTranslation(),
        labelKey: this.assetsLockerKey,
        placeholder: 'Assets',
        loadingTranslation: this.isLoadingAssetsTranslations(),
        component: AssetsLockerLinkComponent,
        binding: {
          movie: this.movie(),
          hideLabel: true,
          loadingTranslation: this.isLoadingAssetsTranslations(),
          labelKey: this.assetsLockerKey,
          labelButton: this.assetsLockerLinkTranslation(),
          classes: 'movie-site_nav-action_menu-action button fill'
        }
      });
      /*   */
      //
    }

    return navActions;
  }

  loadCommunityElements() {
    const movie = this.movie();
    if (movie == null) {
      this.debugLog('Skipping community elements load: movie is null');
      return;
    }

    if (this.featureFilmInteractionToken) {
      this.debugLog('Feature film interaction token is available');
      this.filmInteractionIsEnabled.set(true);

      if (!this.filmInteractionComponentDesktop) {
        this.filmInteractionComponentRef.instance.movie = movie;
      } else {
        this.filmInteractionComponentRef = this.filmInteractionComponentDesktop.createComponent(
          FeatureFilmInteractionsComponent
        );
        this.filmInteractionComponentRef.instance.movie = movie;
        this.filmInteractionComponentRef.instance.orientation = 'vertical';
        this.filmInteractionComponentRef.instance.classBinding = 'movie-site-film-interactions-desktop';
      }

      if (!this.filmInteractionComponentMobile) {
        this.filmInteractionComponent2Ref.instance.movie = movie;
      } else {
        this.filmInteractionComponent2Ref = this.filmInteractionComponentMobile.createComponent(
          FeatureFilmInteractionsComponent
        );
        this.filmInteractionComponent2Ref.instance.movie = movie;
        this.filmInteractionComponent2Ref.instance.orientation = 'horizontal';
        this.filmInteractionComponent2Ref.instance.classBinding = 'movie-site-film-interactions-mobile';
      }
    } else {
      this.debugLog('Community film interaction token/component not available');
    }
  }

  openEditor() {
    if (this.isEditEnabled()) {
      window.scrollTo({ behavior: 'smooth', top: 0 });
      this.movieSiteEditorService.open();
    }
  }

  get isNewsEnabled() {
    return !this.appHasOnlyMovies && (this.movie()?.newsPosts || 0 > 0);
  }

  private getGallerySlidesPerView():
    | {
        0: number;
        480: number;
        1024: number;
        1400: number;
      }
    | undefined {
    const slidesAtLargestBreakpoint = getChannelMovieProp(
      'movieSite.gallery.numberOfImages',
      this.channelService.channel,
      this.movie()
    );

    if (slidesAtLargestBreakpoint === undefined) {
      return undefined;
    }

    const safeSlidesAtLargestBreakpoint = Math.max(1, slidesAtLargestBreakpoint);

    return {
      0: Math.max(1, safeSlidesAtLargestBreakpoint - 3),
      480: Math.max(1, safeSlidesAtLargestBreakpoint - 2),
      1024: Math.max(1, safeSlidesAtLargestBreakpoint - 1),
      1400: safeSlidesAtLargestBreakpoint
    };
  }

  private debugLog(step: string, context?: unknown): void {
    this.logger.log(step, context ?? '');
  }
}

export interface SectionHyperLink {
  label: string;
  placeholder: string;
  ref: string | undefined;
}
